import { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Spinner, Nav, Table } from 'react-bootstrap';
import { getMyFastrackNodes } from '../../api/fastrackApi';

const accent = '#cc66ff';
const cardDark = { background: 'linear-gradient(135deg, #0d0d0d 0%, #1a0033 100%)', color: '#fff', border: '1px solid #9933ff44' };

const SlotBox = ({ node, label }) => {
  const filled = !!node;
  return (
    <div style={{
      flex: 1,
      border: `2px dashed ${filled ? accent : '#444'}`,
      borderRadius: 10,
      padding: '10px 8px',
      textAlign: 'center',
      background: filled ? '#1a003388' : '#0d0d0d55',
      minWidth: 110,
    }}>
      <div style={{ fontSize: 11, color: '#aaa', marginBottom: 4 }}>{label} Slot</div>
      {filled ? (
        <>
          <div style={{ color: accent, fontWeight: 600, fontSize: 13 }}>{node.userId?.name || '—'}</div>
          <div style={{ color: '#aaa', fontSize: 11 }}>{node.userId?.userCode}</div>
          <div style={{ color: '#888', fontSize: 10, marginTop: 2 }}>
            {new Date(node.createdAt).toLocaleDateString()}
          </div>
        </>
      ) : (
        <div style={{ color: '#555', fontSize: 12, marginTop: 4 }}>Waiting...</div>
      )}
    </div>
  );
};

const ActiveNodeCard = ({ node }) => {
  const leftFilled = !!node.leftChildId;
  const rightFilled = !!node.rightChildId;
  const filledCount = (leftFilled ? 1 : 0) + (rightFilled ? 1 : 0);

  return (
    <Card style={{ ...cardDark, marginBottom: 16 }}>
      <Card.Body>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <Badge bg="success" className="me-2">Active Node</Badge>
            {node.position && (
              <Badge style={{ background: '#9933ff' }}>
                {node.position === 'left' ? 'Left' : 'Right'} child
              </Badge>
            )}
            {!node.parentId && <Badge style={{ background: '#cc6600' }}>Root</Badge>}
          </div>
          <div style={{ fontSize: 12, color: '#aaa' }}>
            Placed: {new Date(node.createdAt).toLocaleString()}
          </div>
        </div>

        {/* Cycle visualisation */}
        <div style={{ textAlign: 'center', marginBottom: 12 }}>
          <div style={{
            display: 'inline-block',
            background: '#9933ff33',
            border: `2px solid ${accent}`,
            borderRadius: 10,
            padding: '6px 20px',
            color: accent,
            fontWeight: 700,
            fontSize: 14,
            marginBottom: 8,
          }}>
            You
          </div>
          <div style={{ color: '#555', fontSize: 20, lineHeight: 1 }}>/ \</div>
        </div>

        <div className="d-flex gap-3 justify-content-center">
          <SlotBox node={node.leftChildId} label="Left" />
          <SlotBox node={node.rightChildId} label="Right" />
        </div>

        <div className="mt-3 d-flex justify-content-between align-items-center">
          <div style={{ fontSize: 12, color: '#aaa' }}>
            Slots filled: <strong style={{ color: filledCount === 2 ? '#66ff99' : accent }}>{filledCount} / 2</strong>
          </div>
          <div style={{ fontSize: 12, color: '#aaa' }}>
            20% bonus on completion: <strong style={{ color: '#66ff99' }}>
              {node.planId?.amount ? `${(node.planId.amount * 0.2).toFixed(2)} USDT` : '—'}
            </strong>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
};

const NodeTimelineItem = ({ node, index }) => {
  const isComplete = node.status === 'complete';
  return (
    <div style={{ display: 'flex', gap: 16, marginBottom: 8 }}>
      {/* Spine */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 32 }}>
        <div style={{
          width: 32, height: 32, borderRadius: '50%',
          background: isComplete ? '#9933ff' : '#1a6600',
          border: `2px solid ${isComplete ? accent : '#66ff99'}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontWeight: 700, fontSize: 13, flexShrink: 0,
        }}>
          {index + 1}
        </div>
        <div style={{ flex: 1, width: 2, background: '#333', minHeight: 8 }} />
      </div>

      {/* Content */}
      <Card style={{ ...cardDark, flex: 1, marginBottom: 0 }}>
        <Card.Body className="py-2 px-3">
          <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
            <div>
              <Badge bg={isComplete ? 'secondary' : 'success'} className="me-2">
                {isComplete ? 'Complete' : 'Active'}
              </Badge>
              {node.position && (
                <span style={{ color: '#aaa', fontSize: 12 }}>
                  {node.position === 'left' ? 'Left' : 'Right'} child
                  {node.parentId ? '' : ' (Root)'}
                </span>
              )}
              {!node.parentId && <span style={{ color: '#aaa', fontSize: 12 }}>Root position</span>}
            </div>
            <div style={{ fontSize: 11, color: '#777' }}>
              Placed: {new Date(node.createdAt).toLocaleString()}
            </div>
          </div>

          <div className="d-flex gap-4 mt-2 flex-wrap">
            <div style={{ fontSize: 12 }}>
              <span style={{ color: '#aaa' }}>Left: </span>
              {node.leftChildId
                ? <span style={{ color: accent }}>{node.leftChildId.userId?.name} ({node.leftChildId.userId?.userCode})</span>
                : <span style={{ color: '#555' }}>Empty</span>}
            </div>
            <div style={{ fontSize: 12 }}>
              <span style={{ color: '#aaa' }}>Right: </span>
              {node.rightChildId
                ? <span style={{ color: accent }}>{node.rightChildId.userId?.name} ({node.rightChildId.userId?.userCode})</span>
                : <span style={{ color: '#555' }}>Empty</span>}
            </div>
          </div>

          {isComplete && (
            <div className="mt-2 d-flex gap-4 flex-wrap" style={{ borderTop: '1px solid #333', paddingTop: 8 }}>
              <div style={{ fontSize: 12 }}>
                <span style={{ color: '#aaa' }}>Bonus earned: </span>
                <strong style={{ color: '#66ff99' }}>{node.bonusAmount} USDT</strong>
              </div>
              <div style={{ fontSize: 12 }}>
                <span style={{ color: '#aaa' }}>Completed: </span>
                <span style={{ color: '#fff' }}>{new Date(node.completedAt).toLocaleString()}</span>
              </div>
              {node.rebirthToNodeId && (
                <div style={{ fontSize: 12 }}>
                  <span style={{ color: '#aaa' }}>Reborn: </span>
                  <span style={{ color: '#66aaff' }}>{new Date(node.rebirthToNodeId.createdAt).toLocaleString()}</span>
                </div>
              )}
            </div>
          )}

          {node.rebirthFromNodeId && (
            <div style={{ fontSize: 11, color: '#9933ff', marginTop: 6 }}>
              Reborn from Node #{index} — completed {new Date(node.rebirthFromNodeId.completedAt).toLocaleDateString()}
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

const PlanPanel = ({ planData }) => {
  const { plan, nodes } = planData;
  const completedNodes = nodes.filter((n) => n.status === 'complete');
  const activeNode = nodes.find((n) => n.status === 'active');
  const planBonusTotal = completedNodes.reduce((s, n) => s + (n.bonusAmount || 0), 0);

  return (
    <div>
      {/* Plan stats */}
      <Row className="g-3 mb-4">
        <Col xs={6} md={3}>
          <Card style={cardDark} className="text-center p-3">
            <div style={{ fontSize: 22, fontWeight: 700, color: '#66ff99' }}>{planBonusTotal.toFixed(2)}</div>
            <div style={{ fontSize: 12, color: '#aaa' }}>Total Bonus (USDT)</div>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card style={cardDark} className="text-center p-3">
            <div style={{ fontSize: 22, fontWeight: 700, color: accent }}>{completedNodes.length}</div>
            <div style={{ fontSize: 12, color: '#aaa' }}>Cycles Completed</div>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card style={cardDark} className="text-center p-3">
            <div style={{ fontSize: 22, fontWeight: 700, color: '#66aaff' }}>{nodes.length}</div>
            <div style={{ fontSize: 12, color: '#aaa' }}>Total Nodes</div>
          </Card>
        </Col>
        <Col xs={6} md={3}>
          <Card style={cardDark} className="text-center p-3">
            <div style={{ fontSize: 22, fontWeight: 700, color: activeNode ? '#66ff99' : '#555' }}>
              {activeNode ? 'Active' : 'None'}
            </div>
            <div style={{ fontSize: 12, color: '#aaa' }}>Current Node</div>
          </Card>
        </Col>
      </Row>

      {/* Active node */}
      {activeNode && (
        <>
          <h6 style={{ color: '#aaa', marginBottom: 12 }}>Current Active Node</h6>
          <ActiveNodeCard node={{ ...activeNode, planId: plan }} />
        </>
      )}

      {/* Rebirth chain timeline */}
      <h6 style={{ color: '#aaa', margin: '24px 0 12px' }}>Full Node History (Rebirth Chain)</h6>
      {nodes.length === 0 ? (
        <div style={{ color: '#555', fontSize: 13 }}>No nodes yet.</div>
      ) : (
        nodes.map((node, i) => <NodeTimelineItem key={node._id} node={node} index={i} />)
      )}

      {/* Bonus log table */}
      {completedNodes.length > 0 && (
        <>
          <h6 style={{ color: '#aaa', margin: '24px 0 12px' }}>Bonus Transaction Log</h6>
          <Card style={cardDark}>
            <Table responsive className="mb-0" style={{ color: '#ddd' }}>
              <thead style={{ borderBottom: '1px solid #9933ff44' }}>
                <tr>
                  <th style={{ color: '#aaa', fontWeight: 500, fontSize: 13 }}>#</th>
                  <th style={{ color: '#aaa', fontWeight: 500, fontSize: 13 }}>Completed</th>
                  <th style={{ color: '#aaa', fontWeight: 500, fontSize: 13 }}>Left Child</th>
                  <th style={{ color: '#aaa', fontWeight: 500, fontSize: 13 }}>Plan Amount</th>
                  <th style={{ color: '#aaa', fontWeight: 500, fontSize: 13 }}>Bonus (20%)</th>
                </tr>
              </thead>
              <tbody>
                {completedNodes.map((node, i) => (
                  <tr key={node._id} style={{ borderBottom: '1px solid #9933ff22' }}>
                    <td style={{ fontSize: 13 }}>{i + 1}</td>
                    <td style={{ fontSize: 13 }}>{new Date(node.completedAt).toLocaleString()}</td>
                    <td style={{ fontSize: 13, color: accent }}>
                      {node.leftChildId?.userId?.name || '—'} ({node.leftChildId?.userId?.userCode || '—'})
                    </td>
                    <td style={{ fontSize: 13 }}>{plan.amount} USDT</td>
                    <td style={{ fontSize: 13, color: '#66ff99', fontWeight: 600 }}>{node.bonusAmount} USDT</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </>
      )}
    </div>
  );
};

const Fastrack = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePlanKey, setActivePlanKey] = useState(null);

  useEffect(() => {
    getMyFastrackNodes()
      .then((res) => {
        setData(res.data);
        if (res.data.plans?.length > 0) {
          setActivePlanKey(res.data.plans[0].plan._id);
        }
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" style={{ color: accent }} />
      </Container>
    );
  }

  const hasPlans = data?.plans?.length > 0;

  return (
    <div style={{ minHeight: '100vh', background: '#0a0a0a', paddingBottom: 40 }}>
      <Container className="pt-4">
        {/* Header */}
        <div className="mb-4">
          <h4 style={{ color: '#fff', fontWeight: 700 }}>
            Global Fastrack Bonus
          </h4>
          <div style={{ color: '#aaa', fontSize: 13 }}>
            Global fastrack income — earn 20% when your Cycle completes, then rebirth and repeat
          </div>
        </div>

        {/* Global summary */}
        {data?.summary && (
          <Row className="g-3 mb-4">
            <Col xs={12} md={4}>
              <Card style={{ ...cardDark, borderColor: '#66ff9944' }} className="text-center p-3">
                <div style={{ fontSize: 28, fontWeight: 800, color: '#66ff99' }}>
                  {data.summary.totalBonusEarned.toFixed(2)}
                </div>
                <div style={{ color: '#aaa', fontSize: 13 }}>Lifetime Bonus Earned (USDT)</div>
              </Card>
            </Col>
            <Col xs={6} md={4}>
              <Card style={cardDark} className="text-center p-3">
                <div style={{ fontSize: 28, fontWeight: 800, color: accent }}>
                  {data.summary.totalCompleted}
                </div>
                <div style={{ color: '#aaa', fontSize: 13 }}>Cycles Completed</div>
              </Card>
            </Col>
            <Col xs={6} md={4}>
              <Card style={cardDark} className="text-center p-3">
                <div style={{ fontSize: 28, fontWeight: 800, color: '#66aaff' }}>
                  {data.summary.totalActive}
                </div>
                <div style={{ color: '#aaa', fontSize: 13 }}>Active Nodes</div>
              </Card>
            </Col>
          </Row>
        )}

        {!hasPlans ? (
          <Card style={cardDark} className="text-center p-5">
            <div style={{ color: '#555', fontSize: 14 }}>
              You have not subscribed to any plan yet.<br />
              Subscribe to a plan to enter the Global Fastrack network.
            </div>
          </Card>
        ) : (
          <>
            {/* Plan tabs */}
            {data.plans.length > 1 && (
              <Nav variant="tabs" className="mb-4" style={{ borderBottom: '1px solid #9933ff44', overflowX: 'auto', overflowY: 'hidden', flexWrap: 'nowrap', WebkitOverflowScrolling: 'touch' }}>
                {data.plans.map(({ plan }) => (
                  <Nav.Item key={plan._id}>
                    <Nav.Link
                      active={activePlanKey === plan._id}
                      onClick={() => setActivePlanKey(plan._id)}
                      style={{
                        color: activePlanKey === plan._id ? accent : '#aaa',
                        background: activePlanKey === plan._id ? '#1a003388' : 'transparent',
                        border: activePlanKey === plan._id ? `1px solid ${accent}` : '1px solid transparent',
                        borderBottom: 'none',
                        cursor: 'pointer',
                      }}
                    >
                      {plan.planName} — {plan.amount} USDT
                    </Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>
            )}

            {data.plans
              .filter(({ plan }) => data.plans.length === 1 || activePlanKey === plan._id)
              .map(({ plan, nodes }) => (
                <PlanPanel key={plan._id} planData={{ plan, nodes }} />
              ))}
          </>
        )}
      </Container>
    </div>
  );
};

export default Fastrack;
